import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clapperboard, Loader2, Clock, Cog, CheckCircle2, ArrowLeft, Play, Download } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = {
  awaiting_payment: { label: "Awaiting payment", icon: Clock, color: "text-amber-400", step: 0 },
  in_queue: { label: "In queue", icon: Clock, color: "text-amber-400", step: 1 },
  in_progress: { label: "In production", icon: Cog, color: "text-[#00F5D4]", step: 2 },
  delivered: { label: "Delivered", icon: CheckCircle2, color: "text-[#00F5D4]", step: 3 },
};

export default function OrderStatus() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/orders/${id}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setError("We couldn't find that order."))
      .finally(() => setLoading(false));
  }, [id]);

  const st = order ? STATUS[order.status] || STATUS.in_queue : null;

  return (
    <div className="min-h-screen bg-[#0A0B10] text-[#F8F9FA]">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#FF2E63]"><Clapperboard className="w-4 h-4 text-white" /></span>
            <span className="font-display font-bold">iTMunch Viral Videos</span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
        {loading && <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#FF2E63]" /></div>}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="text-slate-400">{error}</p>
            <Link to="/" className="mt-4 inline-block text-[#FF2E63]">Back to home</Link>
          </div>
        )}

        {order && !loading && (
          <div data-testid="order-status-page">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Order</p>
            <h1 className="font-display text-3xl font-extrabold mt-1">#{order.id.slice(0, 8).toUpperCase()}</h1>

            <div className={`mt-4 inline-flex items-center gap-2 rounded-full bg-[#12141C] border border-white/10 px-4 py-2 ${st.color}`}>
              <st.icon className="w-4 h-4" /> <span className="text-sm font-semibold">{st.label}</span>
            </div>

            {/* Progress */}
            <div className="mt-8 flex items-center gap-2">
              {["Ordered", "In queue", "In production", "Delivered"].map((label, i) => (
                <div key={label} className="flex-1">
                  <div className={`h-1.5 rounded-full ${i <= st.step ? "bg-[#FF2E63]" : "bg-white/10"}`} />
                  <p className={`mt-2 text-[11px] ${i <= st.step ? "text-white" : "text-slate-600"}`}>{label}</p>
                </div>
              ))}
            </div>

            {order.status === "delivered" && order.video_url && (
              <div className="mt-8 rounded-2xl bg-[#12141C] border border-[#00F5D4]/40 p-6">
                <h2 className="font-display text-xl font-bold flex items-center gap-2"><Play className="w-5 h-5 text-[#00F5D4]" /> Your video is ready!</h2>
                <p className="mt-2 text-sm text-slate-400">Watch, download and post your 9:16 Instagram reel.</p>
                <a href={order.video_url} target="_blank" rel="noreferrer" data-testid="order-video-link"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00F5D4] hover:bg-[#00d9bd] text-[#0A0B10] font-bold px-6 py-3 transition-colors">
                  <Download className="w-4 h-4" /> Open my video
                </a>
              </div>
            )}

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Info label="Package" value={`${order.tier_name} · ${order.videos} video(s)`} />
              <Info label="Amount" value={`$${order.amount}`} />
              <Info label="Expected delivery" value={`Within ${order.turnaround}`} />
              <Info label="Payment" value={order.payment_status === "paid" ? "Paid ✓" : "Pending"} />
              <Info label="Product URL" value={order.product_url} full />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Info = ({ label, value, full }) => (
  <div className={`rounded-xl bg-[#12141C] border border-white/10 p-4 ${full ? "sm:col-span-2" : ""}`}>
    <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-1 text-sm text-slate-200 break-words">{value}</p>
  </div>
);
